/**
 * Seeking Manager Badge Component (Issue #1451)
 *
 * Displays a distinctive badge for seeded fairs that don't have an assigned manager.
 * A fair is considered "seeded" when submitted_by is null.
 *
 * Features:
 * - Subtle pulse animation to draw attention
 * - Purple color scheme for visual distinction
 * - Three size variants (small, medium, large)
 *
 * WCAG 2.1 AA Compliance:
 * - Proper color contrast (purple-100/purple-800)
 * - Semantic aria-label for screen readers
 * - title attribute for additional context
 */

import React from 'react';
import { UserSearch } from 'lucide-react';

interface SeekingManagerBadgeProps {
  /** CSS classes for custom styling */
  className?: string;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Optional click handler (e.g., to show "Apply Now" modal) */
  onClick?: () => void;
}

/**
 * Badge component to indicate that a fair is seeking a manager
 */
const SeekingManagerBadge: React.FC<SeekingManagerBadgeProps> = ({
  className = '',
  size = 'medium',
  onClick
}) => {
  // Size classes
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5',
    medium: 'text-sm px-2.5 py-1',
    large: 'text-base px-3 py-1.5'
  };

  const iconSizes = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-5 h-5'
  };

  const badge = (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-800 font-semibold border border-purple-300 animate-pulse ${sizeClasses[size]} ${onClick ? 'cursor-pointer hover:bg-purple-200 transition-colors' : ''} ${className}`}
      title="This fair is seeking a manager - Apply to manage this event"
      aria-label="Seeking Manager Badge"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      <UserSearch
        className={iconSizes[size]}
        aria-hidden="true"
      />
      <span>Seeking Manager</span>
    </span>
  );

  return badge;
};

export default SeekingManagerBadge;
